import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { getAuditLogs, type AuditLogFilters } from '../services/audit-logs';
import { getErrorMessage } from '../lib/api';

const rsvpLabel: Record<string, string> = {
  attending: 'tilmeldt sig',
  maybe: 'svaret "måske"',
  not_attending: 'afmeldt sig',
};

const isRawRequestAction = (action: string) => /^(GET|POST|PATCH|PUT|DELETE|OPTIONS)\s/.test(action);

const describeAction = (entry: {
  action: string;
  user?: { name?: string | null } | null;
  method: string;
  path: string;
  metadata?: Record<string, unknown> | null;
}) => {
  const userName = entry.user?.name ?? 'En bruger';
  const eventTitle = typeof entry.metadata?.eventTitle === 'string' ? entry.metadata.eventTitle : '';
  const postTitle = typeof entry.metadata?.postTitle === 'string' ? entry.metadata.postTitle : '';
  const receiverName = typeof entry.metadata?.receiverName === 'string' ? entry.metadata.receiverName : '';
  const rsvpStatus = typeof entry.metadata?.rsvpStatus === 'string' ? entry.metadata.rsvpStatus : '';

  switch (entry.action) {
    case 'EVENT_CREATED':
      return `${userName} har oprettet begivenheden${eventTitle ? ` "${eventTitle}"` : ''}`;
    case 'EVENT_UPDATED':
      return `${userName} har redigeret begivenheden${eventTitle ? ` "${eventTitle}"` : ''}`;
    case 'EVENT_RSVP_CREATED':
    case 'EVENT_RSVP_UPDATED':
      return `${userName} har ${rsvpLabel[rsvpStatus] ?? 'opdateret sin tilmelding'}${eventTitle ? ` til "${eventTitle}"` : ''}`;
    case 'EVENT_REMINDER_RECEIVED': {
      const rawChannels = entry.metadata?.channels;
      const channels: string[] = Array.isArray(rawChannels) ? (rawChannels as string[]) : [];
      const channelLabel: Record<string, string> = { app: 'app', push: 'push', mail: 'e-mail' };
      const channelStr = channels.length > 0
        ? ` via ${channels.map((c) => channelLabel[c] ?? c).join(', ')}`
        : '';
      return `${userName} har modtaget en reminder om manglende tilmelding${eventTitle ? ` til "${eventTitle}"` : ''}${channelStr}`;
    }
    case 'POST_LIKED':
      return `${userName} har liket et opslag${postTitle ? `: "${postTitle}"` : ''}`;
    case 'POST_UNLIKED':
      return `${userName} har fjernet like fra et opslag${postTitle ? `: "${postTitle}"` : ''}`;
    case 'POST_CREATED':
      return `${userName} har oprettet et opslag${postTitle ? `: "${postTitle}"` : ''}`;
    case 'POST_UPDATED':
      return `${userName} har redigeret et opslag${postTitle ? `: "${postTitle}"` : ''}`;
    case 'POST_DELETED':
      return `${userName} har slettet et opslag${postTitle ? `: "${postTitle}"` : ''}`;
    case 'MESSAGE_SENT':
      return `${userName} har sendt en besked${receiverName ? ` til ${receiverName}` : ''}`;
    case 'NOTIFICATION_SENT': {
      const recipientId = typeof entry.metadata?.recipientId === 'string' ? entry.metadata.recipientId : null;
      const recipientName = typeof entry.metadata?.recipientName === 'string' ? entry.metadata.recipientName : null;
      const notifType = typeof entry.metadata?.notificationType === 'string' ? entry.metadata.notificationType : '';
      const channel = typeof entry.metadata?.channel === 'string' ? entry.metadata.channel : '';

      const channelLabel: Record<string, string> = { app: 'app', email: 'e-mail', push: 'push' };
      const typeLabel: Record<string, string> = {
        like: 'like på opslag',
        comment: 'kommentar på opslag',
        co_comment: 'medkommentator-notifikation',
        comment_like: 'like på kommentar',
        event_attendee_comment: 'kommentar på begivenhedsopslag',
        event_invite: 'begivenhedsinvitation',
        event_reminder: 'begivenhedspåmindelse',
        message: 'privat besked',
        new_post: 'nyt opslag',
        event_comment: 'kommentar på begivenhed',
      };

      const recipient = recipientName ?? (recipientId ? `bruger ${recipientId.slice(0, 8)}…` : 'en bruger');
      const typeStr = typeLabel[notifType] ?? notifType;
      const channelStr = channelLabel[channel] ?? channel;
      const actor = userName !== 'En bruger' ? `${userName}` : 'Systemet';
      return `${actor} sendte notifikation (${typeStr}) til ${recipient} via ${channelStr}`;
    }
    case 'ADMIN_NOTIFY_ALL': {
      const count = typeof entry.metadata?.notifiedCount === 'number' ? entry.metadata.notifiedCount : '?';
      return `${userName} notificerede alle brugere om opslaget${postTitle ? ` "${postTitle}"` : ''} (${count} modtagere)`;
    }
    default:
      if (isRawRequestAction(entry.action)) {
        return `${userName} udførte ${entry.method} ${entry.path}`;
      }
      return `${userName} udførte handlingen ${entry.action}`;
  }
};

export default function AdminAuditLogsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [userName, setUserName] = useState('');
  const [method, setMethod] = useState('');
  const [path, setPath] = useState('');
  const [eventsOnly, setEventsOnly] = useState(false);
  const [showRequestLogs, setShowRequestLogs] = useState(false);

  useEffect(() => {
    if (user && !user.isAdmin) {
      navigate('/');
    }
  }, [user, navigate]);

  const queryFilters = useMemo<AuditLogFilters>(
    () => ({
      page,
      limit: 50,
      userName: userName || undefined,
      method: method || undefined,
      path: path || undefined,
      action: eventsOnly ? 'EVENT_' : undefined,
      entryType: showRequestLogs ? 'all' : 'action',
    }),
    [page, userName, method, path, eventsOnly, showRequestLogs]
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-audit-logs', queryFilters],
    queryFn: () => getAuditLogs(queryFilters),
  });

  if (!user?.isAdmin) {
    return null;
  }

  const items = data?.items ?? [];
  const pagination = data?.pagination;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-h1 font-heading font-bold">Audit-log</h1>
        <p className="text-sm text-gray-600 mt-2">
          Viser brugerhandlinger i læsbart format med GDPR-minimeret data (anonymiseret IP og uden request-indhold).
        </p>
      </div>

      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-1">
              Bruger (navn)
            </label>
            <input
              id="userName"
              value={userName}
              onChange={(e) => {
                setPage(1);
                setUserName(e.target.value);
              }}
              className="input"
              placeholder="fx Helle"
            />
          </div>

          <div>
            <label htmlFor="method" className="block text-sm font-medium text-gray-700 mb-1">
              Metode
            </label>
            <select
              id="method"
              value={method}
              onChange={(e) => {
                setPage(1);
                setMethod(e.target.value);
              }}
              className="input"
            >
              <option value="">Alle</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PATCH">PATCH</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="path" className="block text-sm font-medium text-gray-700 mb-1">
              Path indeholder
            </label>
            <input
              id="path"
              value={path}
              onChange={(e) => {
                setPage(1);
                setPath(e.target.value);
              }}
              className="input"
              placeholder="fx /api/v1/posts"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={eventsOnly}
              onChange={(e) => {
                setPage(1);
                setEventsOnly(e.target.checked);
              }}
            />
            Kun begivenheder
          </label>

          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showRequestLogs}
              onChange={(e) => {
                setPage(1);
                setShowRequestLogs(e.target.checked);
              }}
            />
            Vis også rå request-logs (GET/POST/PATCH...)
          </label>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-400 rounded-lg">
          <p className="text-sm text-red-600">{getErrorMessage(error)}</p>
        </div>
      )}

      <div className="card">
        {isLoading ? (
          <p>Indlæser audit-log...</p>
        ) : items.length === 0 ? (
          <p className="text-gray-600">Ingen logposter fundet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tidspunkt</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brugerhandling</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bruger</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detaljer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {new Date(entry.createdAt).toLocaleString('da-DK')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{describeAction(entry)}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">
                      <div className="font-medium">{entry.user?.name ?? 'Ukendt'}</div>
                      <div className="text-xs text-gray-500">{entry.user?.email ?? '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {entry.action === 'MESSAGE_SENT' ? (
                        <div className="text-xs leading-5">
                          <div>Fra: <strong>{entry.metadata?.senderName ?? entry.metadata?.senderId ?? '-'}</strong></div>
                          <div>Til: <strong>{entry.metadata?.receiverName ?? entry.metadata?.receiverId ?? '-'}</strong></div>
                          <div>Længde: {entry.metadata?.messageLength ?? '-'} tegn</div>
                        </div>
                      ) : entry.action === 'EVENT_CREATED' || entry.action === 'EVENT_UPDATED' || entry.action === 'EVENT_RSVP_CREATED' || entry.action === 'EVENT_RSVP_UPDATED' || entry.action === 'EVENT_REMINDER_RECEIVED' ? (
                        <div className="text-xs leading-5">
                          <div>Begivenhed: <strong>{entry.metadata?.eventTitle ?? entry.metadata?.eventId ?? '-'}</strong></div>
                          {entry.metadata?.rsvpStatus && <div>RSVP: {entry.metadata.rsvpStatus}</div>}
                        </div>
                      ) : entry.action === 'POST_LIKED' || entry.action === 'POST_UNLIKED' || entry.action === 'POST_CREATED' || entry.action === 'POST_UPDATED' || entry.action === 'POST_DELETED' ? (
                        <div className="text-xs leading-5">
                          <div>Opslag: <strong>{entry.metadata?.postTitle ?? entry.metadata?.postId ?? '-'}</strong></div>
                          {(entry.action === 'POST_CREATED' || entry.action === 'POST_UPDATED' || entry.action === 'POST_DELETED') && <div>Billeder: {entry.metadata?.imageCount ?? 0}</div>}
                        </div>
                      ) : isRawRequestAction(entry.action) ? (
                        <div className="text-xs leading-5">
                          <div>{entry.method}</div>
                          <div className="text-gray-500 break-all">{entry.path}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{entry.statusCode}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{entry.ipAddress ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t pt-4">
            <span className="text-sm text-gray-600">
              Side {pagination.page} af {pagination.totalPages} ({pagination.total} poster)
            </span>
            <div className="flex gap-2">
              <button
                className="btn"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={pagination.page <= 1}
              >
                Forrige
              </button>
              <button
                className="btn"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={!pagination.hasMore}
              >
                Næste
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
